import { useNotification } from 'admin';
import {
  type DocumentType,
  getApiDocumentsV1DocumentsRetrieveQueryKey,
  useApiDocumentsV1DocumentsPartialUpdate,
  useApiDocumentsV1DocumentsPdfCreate,
  useApiDocumentsV1DocumentsSendSigningEmailCreate,
  useQueryClient,
} from 'api';
import { useRef, useState } from 'react';
import { Box, Button, HiddenInput, IconButton, Menu, MenuItem, Stack } from 'ui';
import { DoneOutlined, MoreHorizOutlined } from 'ui/icons';

import { useDocument } from '~/document-generation/context/DocumentContext';
import { useUploadDocument } from '~/document-generation/hooks/useUploadDocument';
import { downloadFile } from '~/document-generation/utils/utils';

import { UpdateAliasDialog } from '..';
import { History } from '../History/History';

type Props = {
  generatePdf: ReturnType<typeof useApiDocumentsV1DocumentsPdfCreate>['mutate'];
  savePdf: ReturnType<typeof useApiDocumentsV1DocumentsPdfCreate>['mutate'];
  isGeneratePdfPending: ReturnType<typeof useApiDocumentsV1DocumentsPdfCreate>['isPending'];
  isSavingPdf: ReturnType<typeof useApiDocumentsV1DocumentsPdfCreate>['isPending'];
  disableSaveButton: boolean;
  resetSaveMutation: () => void;
};

export const ActionButtons = ({
  generatePdf,
  savePdf,
  resetSaveMutation,
  isGeneratePdfPending,
  disableSaveButton,
  isSavingPdf,
}: Props) => {
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [updateAliasDialogOpen, setUpdateAliasDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { document } = useDocument();
  const { upload, isPending: isUploadPending } = useUploadDocument(document.id);
  const { mutate: sendSigningEmail, isPending: isSendSigningEmailPending } =
    useApiDocumentsV1DocumentsSendSigningEmailCreate({
      mutation: {
        onError: () => {
          open?.({ message: 'Failed to send signing email', type: 'error' });
        },
        onSuccess: () => {
          open?.({ message: 'Signing email sent', type: 'success' });
        },
      },
    });

  if (!document) {
    return null;
  }

  const openUpdateAliasDialog = () => {
    setUpdateAliasDialogOpen(true);
  };

  const closeUpdateAliasDialog = () => {
    setUpdateAliasDialogOpen(false);
  };

  const openHistory = () => {
    setIsHistoryOpen(true);
  };

  const closeHistoryDialog = () => {
    setIsHistoryOpen(false);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await upload(file, document.type as DocumentType);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = () => {
    downloadFile(document.file_url, document.file_name);
  };

  const handleCopySigningLink = () => {
    const signginURl = new URL(`/document/${document.id}/sign`, window.origin);
    navigator.clipboard.writeText(signginURl.toString());
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const handleSendSigningEmail = () => {
    sendSigningEmail({ documentId: document.id });
  };

  const disableActions = isGeneratePdfPending || isUploadPending || isSavingPdf;

  return (
    <Stack direction="row" useFlexGap gap={1.5} justifyContent="flex-end" my={1.5} px={1.5}>
      <HiddenInput type="file" ref={fileInputRef} onChange={handleFileSelect} />
      <Box flexWrap="wrap" display="flex" gap={1.5}>
        <Button
          loading={isSavingPdf}
          disabled={!disableSaveButton || disableActions}
          onClick={() =>
            savePdf({
              data: { save: true },
              id: Number(document.id),
            })
          }
        >
          Save
        </Button>
        <Button
          variant="secondary"
          loading={isGeneratePdfPending}
          disabled={disableActions}
          onClick={() => {
            resetSaveMutation();
            generatePdf({
              data: { save: false },
              id: Number(document.id),
            });
          }}
        >
          Regenerate
        </Button>
        <Button disabled={disableActions} variant="secondary" onClick={handleUploadClick}>
          Upload document
        </Button>
        <Button disabled={disableActions} variant="secondary" onClick={handleDownload}>
          Download
        </Button>
        <Button
          disabled={isCopied || disableActions}
          variant="secondary"
          onClick={handleCopySigningLink}
          endIcon={isCopied ? <DoneOutlined /> : null}
        >
          {isCopied ? 'Copied to clipboard' : 'Copy Signing Link'}
        </Button>
        <Button
          variant="secondary"
          disabled={disableActions}
          loading={isSendSigningEmailPending}
          onClick={handleSendSigningEmail}
        >
          Send signing email
        </Button>
      </Box>
      <More openHistory={openHistory} openUpdateAliasDialog={openUpdateAliasDialog} disabled={disableActions} />
      <History open={isHistoryOpen} onClose={closeHistoryDialog} title="So" documentId={document.id} />
      <UpdateAliasDialog
        open={updateAliasDialogOpen}
        documentId={document.id}
        alias={document.alias}
        onClose={closeUpdateAliasDialog}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
          });
        }}
      />
    </Stack>
  );
};

interface MoreProps {
  openHistory: () => void;
  openUpdateAliasDialog: () => void;
  disabled: boolean;
}

const More = ({ openUpdateAliasDialog, disabled }: MoreProps) => {
  const queryClient = useQueryClient();
  const { document } = useDocument();
  const { mutate: updateDocument } = useApiDocumentsV1DocumentsPartialUpdate({
    mutation: {
      onError: () => {
        open?.({ message: 'Failed to mark document as signed', type: 'error' });
      },
      onSuccess: () => {
        if (!document) {
          return;
        }

        open?.({ message: 'Document marked as signed', type: 'success' });
        queryClient.invalidateQueries({
          queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
        });
      },
    },
  });
  const { open } = useNotification();

  const handleMarkAsSigned = () => {
    updateDocument({
      data: {
        manual_fully_signed: true,
      },
      id: document.id,
    });
  };

  return (
    <Menu
      equalMenuWidth={false}
      button={
        <IconButton disabled={disabled}>
          <MoreHorizOutlined titleAccess="Open document actions menu" />
        </IconButton>
      }
    >
      <MenuItem onClick={openUpdateAliasDialog}>Update alias</MenuItem>
      <MenuItem onClick={handleMarkAsSigned} disabled={document?.manual_fully_signed ?? false}>
        Mark document as signed
      </MenuItem>
    </Menu>
  );
};
